/**
 * Resolves the `preferred_language` to store at signup time using the
 * strongest available signal. Used by every signup entry point (web
 * OAuth callback, email signup, native-OAuth post-signup) so that a
 * Korean-phone-verified user landing on /en doesn't end up locked into
 * English notifications forever — and so that a Russian-speaking visitor
 * in Amsterdam isn't silently assigned Korean (see plan: prod incident
 * f1a948b3, NL/ru/kakao → ko).
 *
 * Priority order:
 *  1. phone_country_code  — only present after phone verification, but
 *     when it is, it's the strongest "where the user actually lives"
 *     signal we have.
 *  2. Accept-Language header — the user's browser tells us what they
 *     read. Honors q-values so "ko-KR,ko;q=0.9,en;q=0.8" → ko, not en.
 *  3. URL locale segment — ONLY when it differs from defaultLocale ("ko").
 *     Because routing uses localePrefix:"always" + localeDetection:false,
 *     every anonymous visitor is force-redirected to /ko for Googlebot
 *     determinism, so a "ko" urlLocale carries no real preference. A
 *     non-"ko" urlLocale means the user deliberately switched language.
 *  4. Geo country (signup IP) — a far better guess than the SEO-polluted
 *     "/ko" default for users whose browser language we don't support.
 *  5. Final fallback "en" — a user with zero Korean signal should default
 *     to English, never Korean.
 */

import { getLocaleByCountry } from "./country-locale";
import { isLocale, isPublicLocale, type Locale } from "@/i18n/routing";

export type SupportedLocale = Locale;

// Korea is the SEO/canonical default everyone gets redirected to, so a "ko"
// urlLocale is not a positive preference signal — see priority 3 above.
const DEFAULT_URL_LOCALE: SupportedLocale = "ko";

const PHONE_COUNTRY_TO_LOCALE: Record<string, SupportedLocale> = {
  KR: "ko",
  JP: "ja",
  TW: "zh-TW",
  CN: "zh-CN",
  HK: "zh-TW",
  MO: "zh-TW",
};

export function resolveSignupLocale(opts: {
  urlLocale: string;
  acceptLanguageHeader: string | null | undefined;
  phoneCountryCode?: string | null;
  geoCountry?: string | null;
}): SupportedLocale {
  // 1. Phone country — strongest signal when available.
  if (opts.phoneCountryCode) {
    const mapped = PHONE_COUNTRY_TO_LOCALE[opts.phoneCountryCode.toUpperCase()];
    if (mapped && isPublicLocale(mapped)) return mapped;
  }

  // 2. Accept-Language header (sorted by q-value, then first supported match).
  if (opts.acceptLanguageHeader) {
    const tags = parseAcceptLanguage(opts.acceptLanguageHeader);
    for (const tag of tags) {
      const matched = matchSupportedTag(tag);
      if (matched) return matched;
    }
  }

  // 3. URL locale segment — only a deliberate (non-default) switch counts.
  if (
    opts.urlLocale !== DEFAULT_URL_LOCALE &&
    isLocale(opts.urlLocale)
  ) {
    return opts.urlLocale;
  }

  // 4. Geo country — maps to a supported locale (unmapped countries → "en").
  if (opts.geoCountry) {
    const byCountry = getLocaleByCountry(opts.geoCountry);
    return isPublicLocale(byCountry) ? byCountry : "en";
  }

  // 5. Final fallback: English, not Korean.
  return "en";
}

function parseAcceptLanguage(header: string): string[] {
  return header
    .split(",")
    .map((part) => {
      const [rawTag, ...rest] = part.trim().split(";");
      const tag = rawTag?.trim();
      if (!tag) return null;
      const qDirective = rest.find((s) => s.trim().startsWith("q="));
      const q = qDirective
        ? Number.parseFloat(qDirective.trim().slice(2))
        : 1.0;
      return { tag, q: Number.isFinite(q) ? q : 0 };
    })
    .filter((entry): entry is { tag: string; q: number } => Boolean(entry))
    .sort((a, b) => b.q - a.q)
    .map((entry) => entry.tag);
}

/**
 * BCP-47 태그 → 지원 locale. 클라이언트 브라우저 언어 감지(detect-browser-locale)도 재사용.
 * 숨김 출시 중인 언어(publicLocales 밖)는 자동 감지·리다이렉트 대상에서 뺀다 —
 * 그 언어는 URL 로 직접 들어온 사람에게만 보인다.
 */
export function matchSupportedTag(tag: string): SupportedLocale | null {
  const matched = matchLocaleTag(tag);
  return matched && isPublicLocale(matched) ? matched : null;
}

function matchLocaleTag(tag: string): SupportedLocale | null {
  const lower = tag.toLowerCase();
  if (lower.startsWith("ko")) return "ko";
  if (lower.startsWith("ja")) return "ja";
  // zh-Hans / zh-CN / zh-Hans-CN → zh-CN
  if (lower === "zh-cn" || lower.startsWith("zh-hans") || lower === "zh") {
    return "zh-CN";
  }
  // zh-Hant / zh-TW / zh-Hant-TW / zh-HK / zh-MO → zh-TW
  if (
    lower === "zh-tw" ||
    lower.startsWith("zh-hant") ||
    lower === "zh-hk" ||
    lower === "zh-mo"
  ) {
    return "zh-TW";
  }
  if (lower.startsWith("en")) return "en";
  // 그 밖의 언어(th-TH, vi-VN, id-ID …)는 기본 언어 코드가 지원 locale 이면 그대로
  const base = lower.split("-")[0];
  return isLocale(base) ? base : null;
}
